import { Typography } from "@fullscript/aviary-web";
import type { Headings, MDXMetadata } from "@/utils/server/mdx";
import * as styles from "./DocsRightContent.styles";
import { useActiveHeading } from "@/hooks/useActiveHeading";
import { Link } from "@/aviaryTmp/Link/Link";

// This margin adjustment is for the global header's height
export const MARGIN_ADJUSTMENT = 32;

type Props = {
  headings: Headings;
  meta: MDXMetadata;
};

const DocsRightContent = ({ headings, meta }: Props) => {
  const { related_topics: relatedTopics } = meta;

  const activeHeadingId = useActiveHeading({ headings, topOffset: -100, bottomOffset: -350 });

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, headingId: string) => {
    e.preventDefault();
    const element = document.getElementById(headingId);

    if (element) {
      window.scrollTo({
        top: element.offsetTop - MARGIN_ADJUSTMENT,
        behavior: "smooth",
      });

      // Update the URL without causing a page jump
      window.history.replaceState(null, "", `#${headingId}`);
    }
  };

  const headingElements = headings.map(heading => {
    if (heading.level === 2) {
      return (
        <div key={heading.id} css={styles.headingWrapper}>
          <a
            href={`#${heading.id}`}
            css={[styles.link, activeHeadingId === heading.id && styles.linkActive]}
            onClick={e => handleSmoothScroll(e, heading.id)}
          >
            {heading.text}
          </a>
        </div>
      );
    }
    return (
      <div key={heading.id} css={[styles.headingWrapper, styles.headingChildWrapper]}>
        <a
          href={`#${heading.id}`}
          css={[styles.link, activeHeadingId === heading.id && styles.linkActive]}
          onClick={e => handleSmoothScroll(e, heading.id)}
        >
          {heading.text}
        </a>
      </div>
    );
  });

  const hasHeadings = headings.length > 0;

  return (
    <div css={styles.container}>
      {hasHeadings && (
        <>
          <Typography variant="caption" customCss={styles.heading} isMarginless>
            ON THIS PAGE
          </Typography>
          {headingElements}
        </>
      )}
      {relatedTopics && (
        <div css={[styles.relatedTopics, hasHeadings && styles.gap]}>
          <Typography variant="caption" customCss={styles.heading} isMarginless>
            RELATED TOPICS
          </Typography>
          <ul css={styles.relatedTopicsList}>
            {relatedTopics.map(topic => (
              <li key={topic.text}>
                <Link to={topic.link} isUnderLined={false} css={styles.relatedTopicLink}>
                  <Typography variant="footnote" isMarginless>
                    {topic.text}
                  </Typography>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export { DocsRightContent };
